import { useEffect, useState, type RefObject } from 'react';

/**
 * The INTRINSIC aspect ratio of the `<video>` inside `host`, or null until it
 * is known.
 *
 * Needed because a shared screen has no aspect ratio we can guess. A MacBook
 * shares 16:10, an external monitor 16:9, an ultrawide 21:9, and a shared
 * WINDOW is whatever size that window happened to be — 4:3, square, or taller
 * than it is wide. Assuming 16:9 and letterboxing everything to it is precisely
 * what puts bars around a share that never needed them.
 *
 * It is read from the element rather than from the track because our media
 * contract does not carry dimensions (`TileView` is participant + role + layer),
 * and `videoWidth`/`videoHeight` are the browser's own answer — correct for
 * every codec and simulcast layer without us modelling any of it.
 *
 * `resize` matters as much as `loadedmetadata`: a presenter who switches from
 * sharing a window to sharing a whole screen changes the intrinsic size of a
 * track that never re-mounts, and only `resize` reports that.
 *
 * The MutationObserver covers the video element being swapped underneath us
 * when a track is re-subscribed, which happens on every quality-ladder change.
 */
export function useVideoAspect(host: RefObject<HTMLElement>): number | null {
  const [ratio, setRatio] = useState<number | null>(null);

  useEffect(() => {
    const root = host.current;
    if (root === null) return undefined;

    let video: HTMLVideoElement | null = null;

    const read = (): void => {
      if (video === null) return;
      const { videoWidth, videoHeight } = video;
      /* Zero means metadata has not landed yet; publishing that as a ratio
         would collapse the box to nothing for a frame. */
      if (videoWidth > 0 && videoHeight > 0) setRatio(videoWidth / videoHeight);
    };

    const detach = (): void => {
      if (video === null) return;
      video.removeEventListener('loadedmetadata', read);
      video.removeEventListener('resize', read);
    };

    const attach = (): void => {
      const found = root.querySelector('video');
      if (found === video) return;
      detach();
      video = found;
      if (video === null) {
        /* The share ended. Forget the ratio rather than leaving the box frozen
           at the shape of a screen nobody is sharing any more. */
        setRatio(null);
        return;
      }
      video.addEventListener('loadedmetadata', read);
      video.addEventListener('resize', read);
      read();
    };

    attach();
    const observer = new MutationObserver(attach);
    observer.observe(root, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      detach();
    };
  }, [host]);

  return ratio;
}
